import type { BrandDefinition } from "@school-sales-ai/shared";
import { LEGAL_CONTACT_EMAIL, LEGAL_ENTITY, LEGAL_PROSE_CLASS } from "./LegalPageShell";

type PrivacyNoticeContentProps = {
  brand: BrandDefinition;
  lastUpdated: string;
};

function ContactLink(): React.JSX.Element {
  return <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>;
}

/**
 * The aviso de privacidad handed to PROSPECTS — the people who talk to the
 * agent — not to CRM users.
 *
 * Written against the LFPDPPP published in the DOF on 2025-03-20, which replaced
 * the 2010 law and dissolved the INAI. Article and fraction numbers are
 * deliberately NOT cited: the previous text cited the old law's numbering, and a
 * wrong citation is worse than none. Industry-neutral on purpose, because the
 * same notice serves schools and stores.
 *
 * Every data category listed in §3 exists in the schema. Adding a new kind of
 * personal data to the product means adding it here and bumping
 * `PRIVACY_NOTICE_VERSION`.
 */
export function PrivacyNoticeContent({
  brand,
  lastUpdated,
}: PrivacyNoticeContentProps): React.JSX.Element {
  return (
    <article className={LEGAL_PROSE_CLASS}>
      <h1>Aviso de Privacidad</h1>

      <p>
        En cumplimiento de la Ley Federal de Protección de Datos Personales en
        Posesión de los Particulares, publicada en el Diario Oficial de la
        Federación el 20 de marzo de 2025 (la «Ley»), {LEGAL_ENTITY} pone a su
        disposición el presente Aviso de Privacidad.{" "}
        <strong>Última actualización: {lastUpdated}.</strong>
      </p>

      <h2>1. Identidad del Responsable</h2>
      <p>
        <strong>Razón social:</strong> {LEGAL_ENTITY}
        <br />
        <strong>Correo de contacto: </strong>
        <ContactLink />
      </p>
      <p>
        {LEGAL_ENTITY} es el <strong>Responsable</strong> del tratamiento de los
        datos personales que usted proporciona a través de la plataforma{" "}
        {brand.name}, incluido el asistente virtual con el que conversa en el
        sitio web o en el WhatsApp de la organización de su interés.
      </p>

      <h2>2. Asistente virtual</h2>
      <p>
        Las respuestas que recibe en la conversación las genera un{" "}
        <strong>asistente virtual basado en inteligencia artificial</strong>, no
        una persona. Puede equivocarse. En cualquier momento puede pedir hablar
        con una persona de la organización de su interés.
      </p>

      <h2>3. Datos personales que tratamos</h2>
      <p>Según lo que usted comparta en la conversación, podemos tratar:</p>
      <ul>
        <li>
          <strong>Identificación y contacto:</strong> nombre, número de teléfono
          y correo electrónico.
        </li>
        <li>
          <strong>Ubicación:</strong> ciudad o zona de interés y, cuando realiza
          una compra con entrega a domicilio, el domicilio de entrega.
        </li>
        <li>
          <strong>Su solicitud:</strong> los programas, productos o servicios por
          los que pregunta, la fecha o ciclo de ingreso que le interesa y las
          visitas que agende.
        </li>
        <li>
          <strong>Compras:</strong> los pedidos que realice, sus productos,
          montos y estado de entrega.
        </li>
        <li>
          <strong>Conversación:</strong> el historial de mensajes con el
          asistente virtual y con el personal de la organización, incluidas las
          notas de voz, que se transcriben a texto para poder atenderlas.
        </li>
        <li>
          <strong>Origen del contacto:</strong> la página web desde la que inició
          la conversación o el anuncio de WhatsApp por el que nos contactó.
        </li>
      </ul>
      <p>
        <strong>No solicitamos datos personales sensibles</strong>, como los
        relativos a su estado de salud, origen racial o étnico, creencias
        religiosas, filosóficas o morales, opiniones políticas o preferencia
        sexual. Le pedimos no compartirlos a través del asistente.
      </p>

      <h2>4. Datos de otras personas y de menores de edad</h2>
      <p>
        Si usted nos proporciona datos de otra persona, por ejemplo el nombre, la
        edad y el grado escolar de su hija o hijo para quien solicita
        información, usted declara contar con la facultad para hacerlo, ya sea
        porque ejerce la patria potestad o la tutela, o porque cuenta con el
        consentimiento de esa persona. Esos datos se tratan únicamente para las
        finalidades primarias de este aviso.
      </p>

      <h2>5. Finalidades del tratamiento</h2>
      <p>
        <strong>Finalidades primarias</strong> (necesarias para atender su
        solicitud):
      </p>
      <ul>
        <li>
          Responder sus preguntas sobre la organización de su interés y sus
          programas, productos o servicios.
        </li>
        <li>
          Dar seguimiento a su solicitud de información, admisión o compra:
          agendar visitas, registrar pedidos y coordinar entregas.
        </li>
        <li>
          Hacer llegar su solicitud a la organización de su interés para que su
          personal le atienda directamente.
        </li>
        <li>
          Enviarle confirmaciones y recordatorios relacionados con su solicitud,
          como los de una visita agendada.
        </li>
      </ul>
      <p>
        <strong>Finalidades secundarias</strong> (no son necesarias para atender
        su solicitud y requieren su consentimiento):
      </p>
      <ul>
        <li>
          Enviarle información promocional de la organización de su interés, por
          ejemplo mensajes por WhatsApp.
        </li>
        <li>
          Elaborar estadísticas e informes internos sobre el desempeño del
          servicio.
        </li>
        <li>Mejorar el servicio mediante el análisis de su uso.</li>
      </ul>
      <p>
        Si no desea que sus datos se traten para las finalidades secundarias,
        escriba a <ContactLink /> con el asunto{" "}
        <em>«Oposición a finalidades secundarias»</em>. Para dejar de recibir
        mensajes promocionales por WhatsApp, basta con responder{" "}
        <strong>BAJA</strong> a cualquiera de ellos. Su negativa no será motivo
        para dejar de atender su solicitud.
      </p>
      <p>
        Ni {LEGAL_ENTITY} ni sus proveedores de inteligencia artificial utilizan
        sus datos personales para entrenar modelos de inteligencia artificial.
      </p>

      <h2>6. Transferencias</h2>
      <p>
        Sus datos personales se comunican a la{" "}
        <strong>organización sobre la cual usted solicitó información</strong>{" "}
        (una institución educativa o un comercio) para que le atienda
        directamente y dé continuidad a su solicitud. Esta transferencia es
        necesaria para atender la relación que usted mismo solicitó, por lo que
        no requiere su consentimiento conforme a la Ley. A partir de que la
        recibe, esa organización es responsable del tratamiento que haga de sus
        datos.
      </p>
      <p>
        No transferimos sus datos personales a ningún otro tercero, salvo cuando
        lo requiera una autoridad competente conforme a la ley.
      </p>

      <h2>7. Proveedores que tratan sus datos por nuestra cuenta</h2>
      <p>
        Para prestar el servicio nos apoyamos en proveedores que tratan sus datos
        personales <strong>por cuenta nuestra, siguiendo nuestras
        instrucciones</strong> y sin poder usarlos para fines propios
        (encargados):
      </p>
      <ul>
        <li>Infraestructura en la nube y bases de datos.</li>
        <li>
          Modelos de inteligencia artificial que generan las respuestas del
          asistente y transcriben las notas de voz.
        </li>
        <li>Mensajería (WhatsApp, de Meta Platforms).</li>
        <li>Envío de correo electrónico.</li>
      </ul>
      <p>
        Algunos de estos proveedores se ubican o procesan la información{" "}
        <strong>fuera de México</strong>, principalmente en los Estados Unidos de
        América. Comunicar datos a un encargado es una remisión y no una
        transferencia, por lo que no requiere su consentimiento.
      </p>

      <h2>8. Derechos ARCO</h2>
      <p>
        Usted tiene derecho a{" "}
        <strong>Acceder, Rectificar, Cancelar u Oponerse</strong> al tratamiento
        de sus datos personales (derechos ARCO). Para ejercerlos, envíe una
        solicitud a <ContactLink /> que incluya:
      </p>
      <ul>
        <li>
          <strong>Asunto del correo:</strong> «Solicitud de derecho ARCO — [tipo
          de derecho]», por ejemplo «Solicitud de derecho ARCO — Cancelación».
        </li>
        <li>
          Su <strong>nombre completo</strong> y los datos que permitan
          identificarlo como titular, como el número de teléfono o el correo con
          el que conversó.
        </li>
        <li>
          Una <strong>descripción clara</strong> del derecho que desea ejercer y
          de los datos personales a los que se refiere.
        </li>
      </ul>
      <p>
        Recibirá un <strong>acuse de recibo</strong> en un plazo máximo de{" "}
        <strong>5 días hábiles</strong> a partir de la recepción de su solicitud
        completa, y la respuesta dentro de los plazos que establece la Ley. Si su
        solicitud de <strong>cancelación</strong> resulta procedente, se hará
        efectiva en un plazo máximo de <strong>1 mes natural</strong> contado
        desde nuestra respuesta.
      </p>
      <p>
        Si considera que su derecho a la protección de sus datos personales ha
        sido vulnerado, puede acudir ante la{" "}
        <strong>Secretaría Anticorrupción y Buen Gobierno</strong>, autoridad
        encargada de vigilar el cumplimiento de la Ley.
      </p>

      <h2>9. Revocación del consentimiento</h2>
      <p>
        Puede revocar en cualquier momento el consentimiento que haya otorgado
        para el tratamiento de sus datos personales, siempre que dicho
        tratamiento no sea necesario para cumplir una obligación legal o
        contractual. Para ello, use el mismo canal de la sección anterior:{" "}
        <ContactLink />.
      </p>
      <p>
        La revocación <strong>no tiene efectos retroactivos</strong> y puede
        impedir que sigamos atendiendo su solicitud.
      </p>

      <h2>10. Cookies y almacenamiento en su navegador</h2>
      <p>
        Esta plataforma utiliza únicamente cookies y almacenamiento local del
        navegador <strong>estrictamente necesarios</strong> para funcionar:
        mantener abierta su sesión o su conversación con el asistente. No
        utilizamos cookies publicitarias ni tecnologías de rastreo de terceros.
      </p>
      <p>
        Como son indispensables para prestar el servicio, no se requiere ninguna
        acción de su parte para aceptarlas.
      </p>

      <h2>11. Cambios al aviso de privacidad</h2>
      <p>
        Este Aviso de Privacidad puede modificarse para reflejar cambios
        legislativos, cambios en el servicio o en nuestras prácticas de
        privacidad. Cualquier cambio se publicará en esta página, y la fecha de
        la última actualización que aparece al inicio y al final del documento
        cambiará en consecuencia.
      </p>
      <p>
        <strong>Última actualización: {lastUpdated}.</strong>
      </p>
    </article>
  );
}
